// src/utils/printReceipt.js
//
// 直连USB打印方案：完全绕开Windows打印驱动这一层（之前反复遇到"驱动不认识
// 我们发的内容、打出来乱码"的问题，根源就在这一层"翻译"），改成通过QZ Tray
// 直接跟USB设备本身对话，用设备的硬件身份信息（厂商ID+产品ID+接口+端点）
// 精确定位，把ESC/POS原始指令直接发过去，不经过任何驱动解析。
//
// 这几个参数是通过浏览器控制台实测确认出来的，如果以后换了别的型号的打印机，
// 需要重新用同样的方式查一遍（qz.usb.listDevices / listInterfaces / listEndpoints）
const USB_DEVICE = {
  vendorId: '0483',
  productId: '5743',
  interface: '00',
  endpoint: '01', // 输出方向（电脑→设备），82那个是输入方向，不能用来发送打印内容
};

const ESC = '\x1B';
const GS = '\x1D';
const COMMANDS = {
  init:        ESC + '@',
  alignLeft:   ESC + 'a' + '\x00',
  alignCenter: ESC + 'a' + '\x01',
  boldOn:      ESC + 'E' + '\x01',
  boldOff:     ESC + 'E' + '\x00',
  doubleOn:    GS  + '!' + '\x11',
  doubleOff:   GS  + '!' + '\x00',
  cut:         GS  + 'V' + '\x41' + '\x03',
  lineFeed:    '\n',
};

function padLine(left, right, width = 48) {
  const strWidth = (s) => {
    let w = 0;
    for (const ch of s) w += /[\u4e00-\u9fa5]/.test(ch) ? 2 : 1;
    return w;
  };
  const gap = Math.max(1, width - strWidth(left) - strWidth(right));
  return left + ' '.repeat(gap) + right;
}

// receipt 形状：{ orderNo, date, items: [{ name, quantity, price }], total }
function buildReceiptContent(receipt, { storeName, cashierName, paymentMethod } = {}) {
  const PAYMENT_LABELS = { cash: '现金', card: '刷卡', mobile: '移动支付/KHQR' };

  let content = '';
  content += COMMANDS.init;
  content += COMMANDS.alignCenter;
  content += COMMANDS.doubleOn;
  content += (storeName || '收银小票') + COMMANDS.lineFeed;
  content += COMMANDS.doubleOff;
  content += '--------------------------------' + COMMANDS.lineFeed;

  content += COMMANDS.alignLeft;
  content += `订单号: ${receipt.orderNo || ''}` + COMMANDS.lineFeed;
  content += `时间: ${receipt.date || new Date().toLocaleString('zh-CN')}` + COMMANDS.lineFeed;
  if (cashierName) content += `收银员: ${cashierName}` + COMMANDS.lineFeed;
  content += '--------------------------------' + COMMANDS.lineFeed;

  for (const item of (receipt.items || [])) {
    content += (item.name || '') + COMMANDS.lineFeed;
    const qty = item.quantity || 0;
    const price = parseFloat(item.price || 0);
    content += padLine(`  ${qty} x $${price.toFixed(2)}`, `$${(price * qty).toFixed(2)}`) + COMMANDS.lineFeed;
  }

  content += '--------------------------------' + COMMANDS.lineFeed;
  content += COMMANDS.boldOn;
  content += padLine('合计', `$${parseFloat(receipt.total || 0).toFixed(2)}`) + COMMANDS.lineFeed;
  content += COMMANDS.boldOff;
  if (paymentMethod) {
    content += `支付方式: ${PAYMENT_LABELS[paymentMethod] || paymentMethod}` + COMMANDS.lineFeed;
  }
  content += '--------------------------------' + COMMANDS.lineFeed;
  content += COMMANDS.alignCenter;
  content += '谢谢惠顾' + COMMANDS.lineFeed;
  content += COMMANDS.lineFeed + COMMANDS.lineFeed + COMMANDS.lineFeed;
  content += COMMANDS.cut;

  return content;
}

let qzConnected = false;
async function ensureConnected() {
  if (!window.qz) {
    throw new Error('未检测到QZ Tray的JS库，请确认index.html里引入了qz-tray.js');
  }
  if (qzConnected && window.qz.websocket.isActive()) return;
  if (!window.qz.websocket.isActive()) {
    await window.qz.websocket.connect();
  }
  qzConnected = true;
}

/**
 * 打印小票——直连USB设备发送ESC/POS原始指令，完全绕开Windows打印驱动。
 * 每次发送前先"打开设备→占用接口"，发送完"释放接口→关闭设备"，
 * 不然下次发送可能会因为"接口还被占用"而失败。
 *
 * ⚠️ 中文编码目前用的是浏览器字符串默认的处理方式，还没有专门做GBK编码转换——
 * 大多数国产热敏小票机默认认GBK编码，如果测试下来发现中文部分是乱码（但英文、
 * 数字、边框线条这些是正常的），说明就是编码不对，需要再补一层GBK转码，
 * 到时候再处理，不要一开始就默认这一定是完美的
 */
export async function printReceipt(receipt, { storeName, cashierName, paymentMethod } = {}) {
  await ensureConnected();

  const { vendorId, productId, interface: iface, endpoint } = USB_DEVICE;
  const content = buildReceiptContent(receipt, { storeName, cashierName, paymentMethod });

  await window.qz.usb.claimDevice(vendorId, productId, iface);
  try {
    await window.qz.usb.sendData(vendorId, productId, iface, endpoint, content);
  } finally {
    await window.qz.usb.releaseDevice(vendorId, productId);
  }
}